// =============================================================
// VARIANCE — The Executive Command Center
// File      : Signup.tsx
// Location  : /home/Applications/variance/web/src/pages/Signup.tsx
// Owner     : VARIANCE frontend application only
// Purpose   : Premium split-screen signup page matching the logo.
// =============================================================

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Lock, User, Building2, Phone } from 'lucide-react';

export function Signup() {
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    email: '',
    mobile: '',
    password: '',
  });

  const handleSignup = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Connect to CogniFleet AUTH register
    console.log('Signup attempt:', formData);
  };

  return (
    <div className="flex h-screen w-full" style={{ backgroundColor: '#050505' }}>
      
      {/* LEFT SIDE: Branding (60%) */}
      <div className="hidden lg:flex w-3/5 flex-col items-center justify-center relative overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-red-600 rounded-full mix-blend-screen filter blur-[128px] opacity-20"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gray-500 rounded-full mix-blend-screen filter blur-[128px] opacity-10"></div>

        <div className="relative z-10 flex flex-col items-center max-w-2xl text-center px-8">
          <img 
            src="/assets/images/logo6+-bgr.png" 
            alt="VARIANCE" 
            className="w-full max-w-lg mb-8 drop-shadow-2xl"
          />
          <h2 className="text-2xl font-light tracking-wide text-gray-300 mb-2">
            Focus on What Matters <span className="text-red-600 font-bold">&</span> Act on What Counts.
          </h2>
          <p className="text-sm tracking-widest text-gray-500 uppercase mt-4">
            A Comprehensive View of the Enterprise
          </p>
        </div>
      </div>

      {/* RIGHT SIDE: Signup Form (40%) */}
      <div className="w-full lg:w-2/5 flex items-center justify-center p-8">
        <div className="w-full max-w-md p-8 rounded-2xl bg-[#111111] border border-gray-800 shadow-2xl">
          
          <div className="mb-6 text-center lg:text-left">
            <h1 className="text-3xl font-bold text-white mb-2">Create an Account</h1>
            <p className="text-gray-400 text-sm">Start managing your enterprise data.</p>
          </div>

          <form onSubmit={handleSignup} className="space-y-4">
            
            {/* Full Name and Company Name are now stacked vertically */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Full Name</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User size={18} className="text-gray-500" />
                </div>
                <input 
                  type="text" 
                  required 
                  value={formData.fullName} 
                  onChange={e => setFormData({...formData, fullName: e.target.value})} 
                  className="w-full pl-10 pr-3 py-2 bg-[#050505] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-red-600" 
                  placeholder="John Doe" 
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Company Name</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Building2 size={18} className="text-gray-500" />
                </div>
                <input 
                  type="text" 
                  required 
                  value={formData.companyName} 
                  onChange={e => setFormData({...formData, companyName: e.target.value})} 
                  className="w-full pl-10 pr-3 py-2 bg-[#050505] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-red-600" 
                  placeholder="Alpha Developers" 
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Email Address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail size={18} className="text-gray-500" />
                </div>
                <input 
                  type="email" 
                  required 
                  value={formData.email} 
                  onChange={e => setFormData({...formData, email: e.target.value})} 
                  className="w-full pl-10 pr-3 py-2 bg-[#050505] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-red-600" 
                  placeholder="ceo@cognifleet.in" 
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Mobile (Optional)</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone size={18} className="text-gray-500" />
                </div>
                <input 
                  type="text" 
                  value={formData.mobile} 
                  onChange={e => setFormData({...formData, mobile: e.target.value})} 
                  className="w-full pl-10 pr-3 py-2 bg-[#050505] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-red-600" 
                  placeholder="+91 99999 99999" 
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock size={18} className="text-gray-500" />
                </div>
                <input 
                  type="password" 
                  required 
                  value={formData.password} 
                  onChange={e => setFormData({...formData, password: e.target.value})} 
                  className="w-full pl-10 pr-3 py-2 bg-[#050505] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-red-600" 
                  placeholder="••••••••" 
                />
              </div>
            </div>

            <button 
              type="submit" 
              className="w-full py-3 px-4 bg-red-700 hover:bg-red-600 text-white font-bold rounded-lg transition-all shadow-[0_0_15px_rgba(220,38,38,0.3)] hover:shadow-[0_0_25px_rgba(220,38,38,0.5)] mt-4"
            >
              Create Account
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-500">
            Already have an account? <Link to="/login" className="text-red-500 hover:text-red-400 font-medium">Log In</Link>
          </div>

        </div>
      </div>

    </div>
  );
}
